import React, { useMemo, useState, useEffect, useRef } from "react";
import { AgGridReact } from "ag-grid-react";
import { Filter, X } from "lucide-react";
import Select from "react-select";
import Pagination from "../../../utils/Pagination";
import "@ag-grid-community/styles/ag-grid.css";
import "@ag-grid-community/styles/ag-theme-alpine.css";
import CustomDatePicker from "./CustomDatePicker";
const ListView = ({
  filterColumn,
  setFilterColumn,
  columnOptions,
  filterValue,
  setFilterValue,
  handleFilter,
  clearFilters,
  isFilterApplied,
  gridRef,
  columnDefs,
  rowData,
  // rowData,
  itemsPerPage,
  currentPage,
  setCurrentPage,
  onSelectionChanged,
  defaultColDef,
  loading,
  handleItemsPerPageChange,
  totalCount,
  beforePagination, // optional React node to render above pagination
}) => {
  const isMobile = useMemo(() => window.innerWidth < 768, []);
  const [date, setDate] = useState(null);
  console.log("Row Data:", rowData);

  // Reset selection on data change
  useEffect(() => {
    if (gridRef.current?.api) {
      gridRef.current.api.deselectAll();
    }
  }, [rowData]);

  // internal refs to call columnApi.autoSizeColumns when data changes
  const internalApiRef = useRef(null);
  const internalColumnApiRef = useRef(null);

  // auto-size columns to content when rowData or columnDefs change
  useEffect(() => {
    try {
      const colApi = internalColumnApiRef.current;
      const api = internalApiRef.current;
      if (colApi && api) {
        const allCols = colApi.getAllColumns();
        if (Array.isArray(allCols) && allCols.length > 0) {
          const ids = allCols.map((c) => c.getColId());
          // auto size to content; second param skipHeader=false to include header width
          colApi.autoSizeColumns(ids, false);
        }
      }
    } catch (e) {
      // silent
    }
  }, [rowData, columnDefs]);

  return (
    <div className="flex flex-col h-[75vh] bg-white shadow-lg border border-gray-200 ag-theme-alpine">
      {/* 🔹 Filter Section */}
      <div className="grid grid-cols-1 gap-2 w-full md:flex md:items-end md:gap-3 mb-2 p-2 md:p-4 rounded-lg justify-start lg:justify-end">
        {/* Filter By */}
        <div className="md:w-48">
          <label
            htmlFor="filterColumn"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Filter By
          </label>
          <select
            id="filterColumn"
            value={filterColumn}
            onChange={(e) => setFilterColumn(e.target.value)}
            className="w-full p-2 border border-gray-300 rounded-md focus:ring-brandRed focus:border-brandRed"
          >
            {columnOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        {/* Filter Value + Button */}
        <div className="grid grid-cols-2 md:flex md:w-auto gap-2 md:gap-3">
          <div className="md:w-48">
            <label
              htmlFor="filterValue"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Filter Value
            </label>
            {/* Dynamically show control based on selected filterColumn:
                - If it's a lookup-like column (product/agency/insurer/lifeAssured) derive unique values from rowData and show a dropdown
                - If it's a date-like column (DOB, IssueDate, etc.) show a date input
                - Otherwise show a free text input
            */}
            {(() => {
              // helper: mapping logical filter keys to rowData candidate keys
              const mapping = {
                product: ["ProductName", "product", "Product"],
                agency: ["AgencyName", "Agency", "agency"],
                insurer: ["InsurerName", "Insurer", "InsurerId", "insurer"],
                lifeAssured: ["LifeAssuredName", "LifeAssured", "CustomerName", "lifeAssured"],
              };

              const dateKeys = new Set(["DOB", "dob", "IssueDate", "StartDate", "MaturityDate"]);

              const key = String(filterColumn || "");

              // Detect date field
              if (dateKeys.has(key) || key.toLowerCase().includes("date")) {
                return (
                  <CustomDatePicker
                    value={filterValue ? new Date(filterValue) : null}
                    onChange={(date) => {
                      const formatted = date.toISOString().slice(0, 10); // yyyy-mm-dd
                      setFilterValue(formatted);
                    }}
                  />

                );
              }

              // Detect lookup-like fields by checking mapping or substring matches
              const lookupKey = Object.keys(mapping).find((k) => {
                if (k === key.toLowerCase()) return true;
                // also allow matching when option value equals a mapped row key
                return mapping[k].some((rk) => rk === key || rk.toLowerCase() === key.toLowerCase());
              });

              if (lookupKey) {
                // For lookup fields we want to send the numeric id to the backend while showing a label.
                // Define id and label candidate keys for each logical lookup.
                const lookupDefs = {
                  product: { idKeys: ['ProductId', 'ProductID', 'productId', 'productID'], labelKeys: ['ProductName', 'productName'] },
                  // prefer explicit numeric AgencyId keys first to avoid picking the overwritten `Agency` name
                  agency: { idKeys: ['AgencyId', 'AgencyID', 'Agency'], labelKeys: ['AgencyName', 'AgencyDisplayName'] },
                  insurer: { idKeys: ['InsurerId', 'InsurerID', 'Insurer'], labelKeys: ['InsurerName'] },
                  lifeAssured: { idKeys: ['LifeAssuredId', 'LifeAssuredID', 'LifeAssured', 'Id'], labelKeys: ['LifeAssuredName', 'CustomerName'] },
                };

                const def = lookupDefs[lookupKey] || { idKeys: mapping[lookupKey], labelKeys: mapping[lookupKey] };

                // build a map id -> label (prefer labelKeys, fallback to id as label)
                const idToLabel = new Map();
                (rowData || []).forEach((r) => {
                  if (!r) return;
                  // find id
                  let idVal = null;
                  for (const idk of def.idKeys) {
                    if (r[idk] !== undefined && r[idk] !== null && String(r[idk]).trim() !== '') {
                      idVal = r[idk];
                      break;
                    }
                  }
                  // find label
                  let lbl = null;
                  for (const lk of def.labelKeys) {
                    if (r[lk] !== undefined && r[lk] !== null && String(r[lk]).trim() !== '') {
                      lbl = String(r[lk]);
                      break;
                    }
                  }

                  if (idVal !== null) {
                    const keyStr = String(idVal);
                    if (!idToLabel.has(keyStr)) idToLabel.set(keyStr, lbl || keyStr);
                  } else if (lbl !== null) {
                    // if no id but label exists, use label as both id+label to preserve behavior
                    const keyStr = String(lbl);
                    if (!idToLabel.has(keyStr)) idToLabel.set(keyStr, lbl);
                  }
                });

                const options = Array.from(idToLabel.entries()).map(([id, label]) => ({ value: id, label }));
                options.sort((a, b) => String(a.label).localeCompare(String(b.label)));

                if (options.length === 0) {
                  // fallback to free text when we can't derive values
                  return (
                    <input
                      id="filterValue"
                      type="text"
                      value={filterValue}
                      onChange={(e) => setFilterValue(e.target.value)}
                      onKeyPress={(e) => e.key === "Enter" && handleFilter()}
                      className="w-full p-2 border border-gray-300 rounded-md focus:ring-brandRed focus:border-brandRed"
                      placeholder="Enter filter value"
                    />
                  );
                }

                const selectedOption = options.find((o) => String(o.value) === String(filterValue)) || null;

                return (
                  <Select
                    id="filterValue"
                    options={options}
                    value={selectedOption}
                    onChange={(opt) => setFilterValue(opt ? opt.value : "")}
                    isClearable
                    isSearchable
                    placeholder="Select"
                    styles={{
                      control: (base, state) => ({
                        ...base,
                        boxShadow: state.isFocused ? "0 0 0 1px #EB2B21" : undefined,
                        borderColor: state.isFocused ? "#EB2B21" : "#D1D5DB",
                        minHeight: '38px',
                      }),
                      menu: (base) => ({ ...base, zIndex: 9999 }),
                    }}
                  />
                );
              }

              // default: plain text input
              return (
                <input
                  id="filterValue"
                  type="text"
                  value={filterValue}
                  onChange={(e) => setFilterValue(e.target.value)}
                  onKeyPress={(e) => e.key === "Enter" && handleFilter()}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-brandRed focus:border-brandRed"
                  placeholder="Enter filter value"
                />
              );
            })()}
          </div>

          <div className="flex items-end">
            {!isFilterApplied ? (
              <button
                onClick={handleFilter}
                className="px-4 py-2 mx-2 saveButton flex items-center gap-2 w-full md:w-auto justify-center"
                onMouseDown={(e) => e.preventDefault()}
              >
                <Filter size={18} />
                Filter
              </button>
            ) : (
              <button
                onClick={clearFilters}
                className="px-4 py-2 mx-2 backButton flex items-center gap-2 w-full md:w-auto justify-center"
                onMouseDown={(e) => e.preventDefault()}
                title="Clear filters"
              >
                <X size={18} />
                Clear
              </button>
            )}
          </div>
        </div>
      </div>

      {/* 🔹 Grid Section (Scrollable only this part) */}
      {/* Grid Section */}
      <div className="flex-1 w-full overflow-y-auto min-h-0 relative">
        <div
          className="ag-theme-alpine w-full overflow-x-auto"
          style={{ minWidth: "700px", minHeight: isMobile ? "auto" : "50vh" }}
        >
          {/* Spinner Overlay */}
          {loading && (
            <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-80 z-10">
              <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-b-4 border-brandRed"></div>
            </div>
          )}

          {/* AG Grid */}
          {columnDefs && columnDefs.length > 0 && (
            <AgGridReact
              key={columnDefs.length}
              ref={gridRef}
              rowData={rowData}
              columnDefs={columnDefs}
              defaultColDef={defaultColDef}
              rowSelection="multiple"
              suppressRowClickSelection={true}
              onSelectionChanged={onSelectionChanged}
              headerHeight={50}
              rowHeight={40}
              getRowNodeId={(data) => data.id}
              onGridReady={(params) => {
                // store internal api/columnApi for autosize
                internalApiRef.current = params.api;
                internalColumnApiRef.current = params.columnApi;
                // also make sure parent's ref is usable
                if (gridRef && gridRef.current && typeof gridRef.current === 'object') {
                  gridRef.current.api = params.api;
                  gridRef.current.columnApi = params.columnApi;
                }
                // try to auto-size on ready
                try {
                  const allCols = params.columnApi.getAllColumns();
                  if (Array.isArray(allCols) && allCols.length > 0) {
                    const ids = allCols.map((c) => c.getColId());
                    params.columnApi.autoSizeColumns(ids, false);
                  }
                } catch (e) {
                  // ignore
                }
              }}
              suppressScrollOnNewData={false}
              domLayout="autoHeight"
              ensureDomOrder={true}
              suppressCellFocus={true}
            />
          )}
        </div>
      </div>


      {/* 🔹 Before Pagination slot (optional) */}
      {beforePagination && (
        <div className="p-2 border-t border-gray-200 bg-white">
          {beforePagination}
        </div>
      )}

      {/* 🔹 Pagination Section */}
      <div className="hidden md:flex flex-col sm:flex-row justify-around items-center p-4 gap-2 border-t border-gray-200 bg-white">
        <div>Total Count: {totalCount}</div>
        <Pagination
          totalCount={totalCount}
          pageSize={itemsPerPage}
          currentPage={currentPage}
          onPageChange={(page) => setCurrentPage(page)}
          onItemsPerPageChange={handleItemsPerPageChange}
        />
      </div>

      {/* 🔹 Mobile Footer */}
      <div className="p-2 text-gray-950 block md:hidden border-t border-gray-200 bg-white">
        Total Count: {totalCount}
      </div>
    </div>

  );
  {/* Make AG Grid header sticky within the scrollable grid area so the header stays visible while scrolling */ }
  <style>{`.ag-theme-alpine .ag-header { position: sticky; top: 0; z-index: 20; background: white; } .ag-theme-alpine .ag-header-cell { background: white; }`}</style>
};

export default ListView;
